'use client';

import { useEffect, useState } from "react";
import Image from "next/image";
import styles from "./PhotographerMedias.module.css";
import DropdownTags from "../DropdownTags/DropdownTags";
import BottomAside from "../BottomAside/BottomAside";
import MediaModal from "../MediaModal/MediaModal";

/**
 * PhotographerMedias component
 *
 * Displays a photographer's media (images/videos) grid with sorting, likes, and modal viewing.
 *
 * Props:
 * - photographer: {
 *     price: number
 *   }
 * - medias: array of media objects, each containing:
 *     - id: number
 *     - title: string
 *     - image: string (optional)
 *     - video: string (optional)
 *     - likes: number
 *     - date: string
 */
export default function PhotographerMedias({ photographer, medias }) {
    // State to track total likes for all media
    const [totalLikes, setTotalLikes] = useState(0);

    // State for individual media likes and "liked" status
    const [mediaLikes, setMediaLikes] = useState(
        medias.map((media) => ({ id: media.id, likes: media.likes, liked: false }))
    );

    // State to hold sorted media array based on selected sort option
    const [sortedMedias, setSortedMedias] = useState(medias);
    const [sortOption, setSortOption] = useState("Popularité");

    // Modal state for viewing media
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Update total likes whenever individual media likes change
    useEffect(() => {
        const total = mediaLikes.reduce((sum, media) => sum + media.likes, 0);
        setTotalLikes(total);
    }, [mediaLikes]);

    // Load previously liked media from localStorage on mount
    useEffect(() => {
        const savedLikes = JSON.parse(localStorage.getItem("likedMedias") || "[]");
        setMediaLikes((prev) =>
            prev.map((media) =>
                savedLikes.includes(media.id) ? { ...media, liked: true } : media
            )
        );
    }, []);

    // Sort media based on selected sort option (Popularity, Date, Title)
    useEffect(() => {
        const sorted = [...medias].sort((a, b) => {
            if (sortOption === "Popularité") {
                const aLikes = mediaLikes.find((media) => media.id === a.id)?.likes || 0;
                const bLikes = mediaLikes.find((media) => media.id === b.id)?.likes || 0;
                return bLikes - aLikes;
            }
            if (sortOption === "Date") return new Date(b.date) - new Date(a.date);
            if (sortOption === "Titre") return a.title.localeCompare(b.title);
            return 0;
        });
        setSortedMedias(sorted);
    }, [sortOption, mediaLikes, medias]);

    // Toggle like/unlike for a media item
    const toggleLike = async (id) => {
        // Update local state immediately
        setMediaLikes((prev) =>
            prev.map((media) =>
                media.id === id
                    ? {
                          ...media,
                          liked: !media.liked,
                          likes: media.liked ? media.likes - 1 : media.likes + 1,
                      }
                    : media
            )
        );

        // Update localStorage to persist likes
        const savedLikes = JSON.parse(localStorage.getItem("likedMedias") || "[]");
        const alreadyLiked = savedLikes.includes(id);
        const updatedLikes = alreadyLiked
            ? savedLikes.filter((mediaId) => mediaId !== id)
            : [...savedLikes, id];
        localStorage.setItem("likedMedias", JSON.stringify(updatedLikes));

        // Update backend via API
        await fetch(`/api/media/${id}/like`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ liked: !alreadyLiked }),
        });
    };

    // Open modal for a media item at specific index
    const openModal = (index) => {
        setCurrentIndex(index);
        setIsModalOpen(true);
    };

    // Navigate to previous media in modal
    const handlePrev = () => {
        setCurrentIndex((prev) =>
            prev === 0 ? medias.length - 1 : prev - 1
        );
    };

    // Navigate to next media in modal
    const handleNext = () => {
        setCurrentIndex((prev) =>
            prev === medias.length - 1 ? 0 : prev + 1
        );
    };

    return (
        <>
            {/* Dropdown to sort media */}
            <DropdownTags sortBy={sortOption} onChange={setSortOption} />

            {/* Media grid */}
            <section className={styles.mediasGrid}>
                {sortedMedias.map((media, index) => {
                    // Get current media like state
                    const state = mediaLikes.find((med) => med.id === media.id) || { likes: media.likes, liked: false };

                    return (
                        <article key={media.id} className={styles.media}>
                            {/* Display image or video */}
                            {media.image ? (
                                <Image
                                    src={`/assets/${media.image}`}
                                    width={450}
                                    height={450}
                                    alt=""
                                    className={styles.mediaPicture}
                                    onClick={() => openModal(index)}
                                />
                            ) : (
                                <video
                                    width={450}
                                    height={450}
                                    src={`/assets/${media.video}`}
                                    controls
                                    className={styles.mediaPicture}
                                    onClick={() => openModal(index)}
                                />
                            )}

                            {/* Media title and like button */}
                            <div className={styles.mediaContent}>
                                <p className={styles.mediaTitle}>{media.title}</p>
                                <button
                                    className={`${styles.mediaLikes} ${state.liked ? styles.liked : ""}`}
                                    onClick={() => toggleLike(media.id)}
                                >
                                    <p>{state.likes}</p>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        fill={"#901C1C"}
                                        className={styles.heartIcon}
                                    >
                                        <path d="M241 87.1l15 20.7 15-20.7C296 52.5 336.2 32 378.9 32 452.4 32 512 91.6 512 165.1l0 2.6c0 112.2-139.9 242.5-212.9 298.2-12.4 9.4-27.6 14.1-43.1 14.1s-30.8-4.6-43.1-14.1C139.9 410.2 0 279.9 0 167.7l0-2.6C0 91.6 59.6 32 133.1 32 175.8 32 216 52.5 241 87.1z" />
                                    </svg>
                                </button>
                            </div>
                        </article>
                    );
                })}
            </section>

            {/* Media modal for fullscreen view */}
            <MediaModal
                medias={sortedMedias}
                currentIndex={currentIndex}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onPrev={handlePrev}
                onNext={handleNext}
            />

            {/* Sidebar showing total likes and price per day */}
            <BottomAside likes={totalLikes} price={photographer.price} />
        </>
    );
}
